import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { QueryParam, IUseFetchContextData, ResponseModel, ErrorStatus } from '../model/API.model';
import { fetchData, createQueryKey } from '../utils/API.utils';
import { useDateRange } from '../../../features/dateRanges/DateRanges.context';
import { AxiosError } from 'axios';
import { useAuth } from '../../../features/auth/providers/AuthProvider';

const useFetchData = <T>({ params, endpoint, options, includeContext, responseType }: IUseFetchContextData<T>) => {
  const { dateRange } = useDateRange();

  const { logout, userId } = useAuth();

  const contextParams = useMemo(() => {
    if (includeContext) {
      return {
        ...params,
        [QueryParam.DATESTART]: dateRange[0].format(),
        [QueryParam.DATEEND]: dateRange[1].format(),
      };
    } else {
      return { ...params };
    }
  }, [params, dateRange, includeContext]);

  const queryKey = createQueryKey(endpoint, contextParams, userId);

  const { data, error, isLoading, isPreviousData, isSuccess, isFetching, refetch } = useQuery<
    ResponseModel<T> | undefined,
    AxiosError
  >({
    queryKey: queryKey, // Pass the query key as an array
    queryFn: () => fetchData<ResponseModel<T>>(endpoint, contextParams, responseType),
    enabled: options?.enabled,
    refetchOnWindowFocus: false,
    keepPreviousData: options?.keepPreviousData,
    refetchInterval: (options?.refetchInterval as number) || false,
    retry: options?.retry || false,
    ...(options?.staleTime !== undefined ? { staleTime: options.staleTime } : {}),
    ...(options?.cacheTime !== undefined ? { cacheTime: options.cacheTime } : {}),
  });

  useEffect(() => {
    if ((error?.request?.status as unknown as ErrorStatus) === ErrorStatus.UNAUTHORIZED && logout) {
      logout();
    }
  }, [error?.request?.status, logout]);

  return { data, error, isLoading, isPreviousData, isSuccess, isFetching, refetch };
};
export default useFetchData;
